import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { authApi, therapistsApi, clientsApi } from '@/api/endpoints';
import { api } from '@/api/http';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BRAZILIAN_STATES } from '@/lib/brazilian-states';
import { LANGUAGES } from '@/lib/specialties';
import { CityAutocomplete } from '@/components/public/city-autocomplete';
import { AvatarUpload } from '@/components/dashboard/avatar-upload';
import { EditableName } from '@/components/dashboard/editable-name';

export const Route = createFileRoute('/dashboard/perfil')({ component: PerfilPage });

function PerfilPage() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const isTherapist = !!me?.therapistProfile;

  if (!me) return <p className="text-slate-500 text-sm">Carregando...</p>;
  if (isTherapist) return <TherapistProfile />;
  return <ClientProfile />;
}

/* ─── TERAPEUTA ───────────────────────────────────────────────────────────── */

const DAY_LABELS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

type Window = { startTime: string; endTime: string };
type DaySlots = { dayOfWeek: number; enabled: boolean; windows: Window[] };

// ─── Education / títulos ───────────────────────────────────────────────────
const LEVEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'GRADUACAO', label: 'Graduação' },
  { value: 'POS_GRADUACAO', label: 'Pós-graduação' },
  { value: 'ESPECIALIZACAO', label: 'Especialização' },
  { value: 'MBA', label: 'MBA' },
  { value: 'MESTRADO', label: 'Mestrado' },
  { value: 'DOUTORADO', label: 'Doutorado' },
  { value: 'CURSO', label: 'Curso' },
  { value: 'EXTENSAO', label: 'Extensão' },
  { value: 'OUTRO', label: 'Outro' },
];
const LEVEL_LABEL: Record<string, string> = Object.fromEntries(
  LEVEL_OPTIONS.map((o) => [o.value, o.label]),
);
// Ordenação visual: cronologia da carreira (mais antigo primeiro).
// Título sem ano vai pro final; empate resolvido pela prioridade do nível.
const LEVEL_PRIORITY: Record<string, number> = {
  DOUTORADO: 0, MESTRADO: 1, MBA: 2, POS_GRADUACAO: 3, ESPECIALIZACAO: 4,
  GRADUACAO: 5, CURSO: 6, EXTENSAO: 7, OUTRO: 8,
};

function TherapistProfile() {
  const { data: profile } = useQuery({
    queryKey: ['my-therapist-profile'],
    queryFn: () => api.get('therapists/me/profile').json<any>(),
  });

  if (!profile) return <p className="text-slate-500 text-sm">Carregando...</p>;

  return (
    <div className="space-y-5">
      {/* Avatar grande topo */}
      <div className="flex items-center gap-4">
        <AvatarUpload
          currentUrl={profile.avatarUrl ?? profile.user?.image}
          fallbackInitial={profile.user?.name}
          invalidateQueryKeys={[['my-therapist-profile'], ['me'], ['auth-me']]}
          size={64}
        />
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">
            <EditableName
              value={profile.user?.name}
              fallback="Sem nome"
              className="text-2xl font-extrabold"
              invalidateQueryKeys={[['my-therapist-profile'], ['me'], ['auth-me']]}
            />
          </h1>
          <p className="text-slate-500 text-sm mt-1">Dados profissionais e documentos</p>
        </div>
      </div>

      <ProfessionalInfoCard profile={profile} />
      <FiscalIntegrationCard />
      <TranscriptionTogglesCard profile={profile} />
      <EducationCard items={profile.education ?? []} />
      <AvailabilityCard therapistId={profile.id} />
    </div>
  );
}

/**
 * T-01 — Opt-in do terapeuta pra transcrição automática das próprias sessões.
 * Habilitar aqui não ativa nada sozinho: o sistema só grava quando o cliente
 * daquela sessão tiver `recordingConsent=true` também (T-03).
 */
function TranscriptionTogglesCard({ profile }: { profile: any }) {
  const qc = useQueryClient();
  const enabled = !!profile?.transcriptionEnabled;
  const m = useMutation({
    mutationFn: (next: boolean) => therapistsApi.setTranscription(next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-therapist-profile'] }),
  });
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <header>
        <h2 className="text-lg font-bold text-slate-800">Transcrição automática de sessões</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Quando habilitado, o sistema transcreve automaticamente as suas sessões — online (via
          Google Meet) e presencial (gravação pelo celular). A funcionalidade só age em sessões
          onde o <strong>cliente também tiver autorizado</strong> a gravação.
        </p>
      </header>
      <div className="flex items-start gap-3">
        <input
          id="transcriptionEnabled"
          type="checkbox"
          checked={enabled}
          disabled={m.isLoading}
          onChange={(e) => m.mutate(e.target.checked)}
          className="mt-0.5 size-4"
        />
        <label htmlFor="transcriptionEnabled" className="text-sm text-slate-700 leading-relaxed">
          Habilitar transcrição automática das minhas sessões. A transcrição vira rascunho do
          prontuário; o áudio cru é apagado em 7 dias (LGPD Art. 11 — dado sensível).
        </label>
      </div>
      {m.isError && <p className="text-xs text-red-600">Não foi possível atualizar. Tente de novo.</p>}
    </section>
  );
}

/* ─── Focus NFe — integração fiscal (só aparece se o admin habilitou) ──────── */

function FiscalIntegrationCard() {
  const qc = useQueryClient();
  const [senha, setSenha] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    inscricaoMunicipal: '', fiscalCep: '', fiscalLogradouro: '', fiscalNumero: '',
    fiscalComplemento: '', fiscalBairro: '', fiscalMunicipio: '', fiscalUf: '', fiscalCodigoMunicipio: '',
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: fiscal } = useQuery({
    queryKey: ['my-fiscal'],
    queryFn: () => api.get('therapists/me/fiscal').json<any>(),
  });

  useEffect(() => {
    if (fiscal) {
      setForm((f) => ({
        ...f,
        inscricaoMunicipal: fiscal.inscricaoMunicipal ?? '',
        fiscalCep: fiscal.fiscalCep ?? '',
        fiscalLogradouro: fiscal.fiscalLogradouro ?? '',
        fiscalNumero: fiscal.fiscalNumero ?? '',
        fiscalComplemento: fiscal.fiscalComplemento ?? '',
        fiscalBairro: fiscal.fiscalBairro ?? '',
        fiscalMunicipio: fiscal.fiscalMunicipio ?? '',
        fiscalUf: fiscal.fiscalUf ?? '',
        fiscalCodigoMunicipio: fiscal.fiscalCodigoMunicipio ?? '',
      }));
    }
  }, [fiscal]);

  const save = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('file', file as File);
      fd.append('senhaCertificado', senha);
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      return api.put('therapists/me/fiscal', { body: fd, timeout: 60_000 }).json<any>();
    },
    onSuccess: () => {
      setErrorMsg(null); setFile(null); setSenha('');
      qc.invalidateQueries({ queryKey: ['my-fiscal'] });
    },
    onError: async (err: any) => {
      let m = 'Falha ao salvar dados fiscais.';
      try { const b = await err?.response?.json?.(); if (b?.message) m = Array.isArray(b.message) ? b.message.join(' · ') : b.message; } catch { /* noop */ }
      setErrorMsg(m);
    },
  });

  // Autocomplete de endereço via ViaCEP ao completar 8 dígitos
  const onCepBlur = async () => {
    const cep = form.fiscalCep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await r.json();
      if (d.erro) return;
      setForm((f) => ({
        ...f,
        fiscalLogradouro: f.fiscalLogradouro || d.logradouro || '',
        fiscalBairro: f.fiscalBairro || d.bairro || '',
        fiscalMunicipio: f.fiscalMunicipio || d.localidade || '',
        fiscalUf: f.fiscalUf || d.uf || '',
        fiscalCodigoMunicipio: d.ibge || f.fiscalCodigoMunicipio,
      }));
    } catch { /* lookup best-effort */ }
  };

  // Não habilitado pelo admin → seção não aparece
  if (!fiscal || !fiscal.focusNfeEnabled) return null;

  const status = fiscal.focusNfeStatus as string;
  const statusBadge =
    status === 'ACTIVE' ? { t: '✓ Ativo — emissão automática', c: 'bg-teal-50 text-teal-700 border-teal-200' } :
    status === 'ERROR' ? { t: '✗ Erro no cadastro', c: 'bg-red-50 text-red-700 border-red-200' } :
    { t: '⏳ Aguardando seus dados', c: 'bg-amber-50 text-amber-700 border-amber-200' };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h2 className="text-lg font-bold text-slate-800">Integração Fiscal (NFSe automática)</h2>
        <span className={cn('text-xs px-2.5 py-1 rounded-full border', statusBadge.c)}>{statusBadge.t}</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Habilitada pelo administrador. Preencha o endereço fiscal da empresa e envie o
        certificado digital A1 (.pfx) — emitiremos a NFSe das suas sessões automaticamente.
        O certificado <strong>não fica armazenado</strong> em nossos servidores.
      </p>

      {status === 'ACTIVE' && fiscal.certificadoValidoAte && (
        <p className="text-xs text-slate-600 mb-4">
          Certificado válido até{' '}
          <strong>{new Date(fiscal.certificadoValidoAte).toLocaleDateString('pt-BR')}</strong>.
          Reenvie um novo antes de expirar.
        </p>
      )}
      {status === 'ERROR' && fiscal.focusNfeError && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
          {fiscal.focusNfeError}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="CEP *">
          <input className="input" value={form.fiscalCep}
            onChange={set('fiscalCep')} onBlur={onCepBlur} placeholder="00000-000" />
        </Field>
        <Field label="Inscrição Municipal">
          <input className="input" value={form.inscricaoMunicipal} onChange={set('inscricaoMunicipal')} />
        </Field>
        <Field label="Logradouro *">
          <input className="input" value={form.fiscalLogradouro} onChange={set('fiscalLogradouro')} />
        </Field>
        <Field label="Número *">
          <input className="input" value={form.fiscalNumero} onChange={set('fiscalNumero')} />
        </Field>
        <Field label="Complemento">
          <input className="input" value={form.fiscalComplemento} onChange={set('fiscalComplemento')} />
        </Field>
        <Field label="Bairro *">
          <input className="input" value={form.fiscalBairro} onChange={set('fiscalBairro')} />
        </Field>
        <Field label="Município *">
          <input className="input" value={form.fiscalMunicipio} onChange={set('fiscalMunicipio')} />
        </Field>
        <Field label="UF *">
          <input className="input" value={form.fiscalUf} onChange={set('fiscalUf')} maxLength={2} placeholder="SP" />
        </Field>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-3">
        <Field label="Certificado A1 (.pfx / .p12) *">
          <input type="file" accept=".pfx,.p12"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-teal-50 file:text-teal-700" />
          {file && <span className="text-[11px] text-teal-600 mt-1 block">📎 {file.name}</span>}
        </Field>
        <Field label="Senha do certificado *">
          <input className="input" type="password" value={senha} onChange={(e) => setSenha(e.target.value)} />
        </Field>
      </div>

      {errorMsg && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{errorMsg}</div>
      )}

      <div className="mt-4">
        <Button
          onClick={() => save.mutate()}
          disabled={save.isPending || !file || !senha}
          className="bg-teal-500 hover:bg-teal-600 text-white"
        >
          {save.isPending ? 'Enviando dados fiscais...' : status === 'ACTIVE' ? 'Atualizar dados fiscais' : 'Ativar emissão automática'}
        </Button>
      </div>
      <style>{`.input{width:100%;background:white;border:1px solid rgb(226 232 240);border-radius:6px;height:38px;padding:0 12px;outline:none;font-size:14px}.input:focus{box-shadow:0 0 0 2px rgb(112 208 192)}`}</style>
    </section>
  );
}

function ProfessionalInfoCard({ profile }: { profile: any }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const { register, handleSubmit, reset, watch } = useForm<any>();
  const watchedState = watch('state');

  // FB-3 — tags de sub-especialização (state controlado, não vai pelo react-hook-form
  // porque é array editável dinamicamente)
  const [specializations, setSpecializations] = useState<string[]>([]);
  const [specInput, setSpecInput] = useState('');
  const [languages, setLanguages] = useState<string[]>(['Português']);

  const toggleLanguage = (lang: string) => {
    setLanguages((prev) => {
      if (prev.includes(lang)) {
        // Não permite ficar com 0 idiomas — mantém pelo menos 1
        return prev.length === 1 ? prev : prev.filter((l) => l !== lang);
      }
      return [...prev, lang];
    });
  };

  useEffect(() => {
    reset({
      bio: profile.bio ?? '',
      crpNumber: profile.crpNumber ?? '',
      cnpj: profile.cnpj ?? '',
      razaoSocial: profile.razaoSocial ?? '',
      sessionPrice: Number(profile.sessionPrice ?? 0),
      sessionDuration: profile.sessionDuration ?? 50,
      modality: profile.modality ?? 'ONLINE',
      city: profile.city ?? '',
      state: profile.state ?? '',
      // FB-2 — links externos
      videoUrl: profile.videoUrl ?? '',
      websiteUrl: profile.websiteUrl ?? '',
      linkedinUrl: profile.linkedinUrl ?? '',
      instagramUrl: profile.instagramUrl ?? '',
      twitterUrl: profile.twitterUrl ?? '',
      youtubeUrl: profile.youtubeUrl ?? '',
      // avatarUrl é gerenciado via <AvatarUpload> em /v1/users/me/avatar, não pelo PATCH do perfil.
    });
    setSpecializations(Array.isArray(profile.specializations) ? profile.specializations : []);
    setLanguages(Array.isArray(profile.languages) && profile.languages.length > 0 ? profile.languages : ['Português']);
  }, [profile, reset]);

  function addSpecialization() {
    const val = specInput.trim();
    if (!val) return;
    if (specializations.some((s) => s.toLowerCase() === val.toLowerCase())) { setSpecInput(''); return; }
    if (specializations.length >= 8) return; // limite razoável
    setSpecializations((prev) => [...prev, val]);
    setSpecInput('');
  }
  function removeSpecialization(s: string) {
    setSpecializations((prev) => prev.filter((x) => x !== s));
  }

  const update = useMutation({
    mutationFn: (data: any) => api.patch('therapists/me', { json: { ...data, specializations, languages } }).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-therapist-profile'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      setEditing(false);
    },
  });

  const money = (v: any) => `R$ ${Number(v ?? 0).toFixed(2)}`;
  const modalityLabel = profile.modality === 'ONLINE' ? 'ONLINE' : profile.modality === 'PRESENCIAL' ? 'PRESENCIAL' : 'HÍBRIDO';

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <header className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Informações profissionais</h2>
          <p className="text-xs text-slate-500 mt-0.5">Editáveis a qualquer momento</p>
        </div>
        {!editing && (
          <Button variant="outline" onClick={() => setEditing(true)} className="text-sm">
            Editar
          </Button>
        )}
      </header>

      {!editing ? (
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
          <Info label="Razão social" value={profile.razaoSocial || '—'} />
          <Info label="CNPJ" value={profile.cnpj || '—'} />
          <Info label="CRP" value={profile.crpNumber || '—'} />
          <Info label="Valor por sessão" value={money(profile.sessionPrice)} />
          <Info label="Duração" value={`${profile.sessionDuration ?? 50} min`} />
          <Info label="Modalidade" value={modalityLabel} />
          <Info label="Cidade/UF" value={[profile.city, profile.state].filter(Boolean).join(', ') || '—'} />
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Especialidades</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(profile.specialties ?? []).length === 0 ? (
                <span className="text-sm text-slate-400">—</span>
              ) : (
                profile.specialties.map((s: string) => (
                  <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-coral-50 text-coral-700">
                    {s}
                  </span>
                ))
              )}
            </div>
          </div>
          {/* FB-3 — sub-especializações (tags livres) */}
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Sub-especializações</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(profile.specializations ?? []).length === 0 ? (
                <span className="text-sm text-slate-400">—</span>
              ) : (
                profile.specializations.map((s: string) => (
                  <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-700">
                    {s}
                  </span>
                ))
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Idiomas atendidos</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(profile.languages ?? ['Português']).map((l: string) => (
                <span key={l} className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-700">
                  {l}
                </span>
              ))}
            </div>
          </div>
          {/* FB-2 — links externos (só mostra os preenchidos) */}
          {(profile.videoUrl || profile.websiteUrl || profile.linkedinUrl || profile.instagramUrl || profile.twitterUrl || profile.youtubeUrl) && (
            <div className="sm:col-span-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Presença online</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {profile.websiteUrl && <ExternalLinkChip href={profile.websiteUrl} label="Site" />}
                {profile.videoUrl && <ExternalLinkChip href={profile.videoUrl} label="Vídeo de apresentação" />}
                {profile.linkedinUrl && <ExternalLinkChip href={profile.linkedinUrl} label="LinkedIn" />}
                {profile.instagramUrl && <ExternalLinkChip href={profile.instagramUrl} label="Instagram" />}
                {profile.twitterUrl && <ExternalLinkChip href={profile.twitterUrl} label="X / Twitter" />}
                {profile.youtubeUrl && <ExternalLinkChip href={profile.youtubeUrl} label="YouTube" />}
              </div>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit((d) => update.mutate(d))} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Razão social"><input {...register('razaoSocial')} className="input" /></Field>
            <Field label="CNPJ"><input {...register('cnpj')} className="input" /></Field>
            <Field label="CRP"><input {...register('crpNumber')} className="input" /></Field>
            <Field label="Valor por sessão (R$)">
              <input type="number" min={10} step={1} placeholder="mínimo R$ 10,00" {...register('sessionPrice', { valueAsNumber: true, min: 10 })} className="input" />
            </Field>
            <Field label="Duração (min)">
              <input type="number" min={30} max={180} {...register('sessionDuration', { valueAsNumber: true })} className="input" />
            </Field>
            <Field label="Modalidade">
              <select {...register('modality')} className="input">
                <option value="ONLINE">Online</option>
                <option value="PRESENCIAL">Presencial</option>
                <option value="HYBRID">Online ou presencial</option>
              </select>
            </Field>
            <Field label="Cidade">
              <CityAutocomplete state={watchedState} registerProps={register('city')} className="input" />
            </Field>
            <Field label="UF">
              <select {...register('state')} className="input">
                <option value="">Selecione...</option>
                {BRAZILIAN_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Bio">
            <textarea {...register('bio')} rows={4} className="input min-h-24 py-2" />
          </Field>

          {/* FB-2 — links externos */}
          <fieldset className="border-t border-slate-100 pt-4 space-y-3">
            <legend className="text-xs font-bold text-slate-500 uppercase tracking-wide">Presença online (opcional)</legend>
            <p className="text-xs text-slate-500">
              Links externos que aparecem no seu perfil público. Vídeos abrem em nova aba —
              não são incorporados aqui.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Site / Blog"><input type="url" {...register('websiteUrl')} placeholder="https://..." className="input" /></Field>
              <Field label="Vídeo de apresentação"><input type="url" {...register('videoUrl')} placeholder="https://youtu.be/... ou https://vimeo.com/..." className="input" /></Field>
              <Field label="LinkedIn"><input type="url" {...register('linkedinUrl')} placeholder="https://linkedin.com/in/..." className="input" /></Field>
              <Field label="Instagram"><input type="url" {...register('instagramUrl')} placeholder="https://instagram.com/..." className="input" /></Field>
              <Field label="X / Twitter"><input type="url" {...register('twitterUrl')} placeholder="https://x.com/..." className="input" /></Field>
              <Field label="YouTube (canal)"><input type="url" {...register('youtubeUrl')} placeholder="https://youtube.com/@..." className="input" /></Field>
            </div>
          </fieldset>

          {/* FB-3 — tags de sub-especialização */}
          <fieldset className="border-t border-slate-100 pt-4 space-y-3">
            <legend className="text-xs font-bold text-slate-500 uppercase tracking-wide">Sub-especializações (opcional)</legend>
            <p className="text-xs text-slate-500">
              Adicione até 8 tags para descrever sua atuação (ex: <em>Junguiana, Arteterapeuta, Gestalt, TCC</em>). Aparecem abaixo do seu nome no perfil público.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {specializations.map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-100">
                  {s}
                  <button
                    type="button"
                    onClick={() => removeSpecialization(s)}
                    className="text-teal-500 hover:text-teal-700"
                    aria-label={`Remover ${s}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={specInput}
                onChange={(e) => setSpecInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addSpecialization();
                  }
                }}
                placeholder="Digite e pressione Enter"
                maxLength={40}
                disabled={specializations.length >= 8}
                className="input flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={addSpecialization}
                disabled={!specInput.trim() || specializations.length >= 8}
              >
                Adicionar
              </Button>
            </div>
            {specializations.length >= 8 && (
              <p className="text-xs text-amber-600">Limite de 8 sub-especializações atingido.</p>
            )}
          </fieldset>

          <fieldset className="border-t border-slate-100 pt-4 space-y-3">
            <legend className="text-xs font-bold text-slate-500 uppercase tracking-wide">Idiomas atendidos</legend>
            <p className="text-xs text-slate-500">
              Marque os idiomas que você pode usar para atender. Pelo menos 1 precisa estar selecionado.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {LANGUAGES.map((lang) => {
                const active = languages.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleLanguage(lang)}
                    className={
                      active
                        ? 'text-xs px-3 py-1.5 rounded-full bg-teal-500 text-white border border-teal-500'
                        : 'text-xs px-3 py-1.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200 hover:border-teal-300'
                    }
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="flex gap-2">
            <Button type="submit" disabled={update.isPending} className="bg-teal-500 hover:bg-teal-600 text-white">
              {update.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
          </div>
        </form>
      )}
      <style>{`.input{width:100%;background:white;border:1px solid rgb(226 232 240);border-radius:6px;height:38px;padding:0 12px;outline:none;font-size:14px}.input:focus{box-shadow:0 0 0 2px rgb(112 208 192)}`}</style>
    </section>
  );
}

function ExternalLinkChip({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 hover:bg-white hover:border-teal-300 hover:text-teal-700 transition"
    >
      {label}
      <span aria-hidden className="text-slate-400">↗</span>
    </a>
  );
}

// Normaliza "HH:MM" para "HH:00". Sessões são em horas cheias e o picker
// é um <select> com 24 opções, mas o backend pode trazer slots antigos
// (com minutos) — trunca aqui pra garantir consistência ao carregar.
function toHourOnly(t: string): string {
  const [h] = (t ?? '').split(':');
  const hh = (h ?? '09').padStart(2, '0');
  return `${hh}:00`;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

type EducationItem = {
  id: string;
  level: string;
  course: string;
  institution: string;
  year: number | null;
  fileName?: string | null;
};

function EducationCard({ items }: { items: EducationItem[] }) {
  const qc = useQueryClient();
  // editing: null = fechado, {} = novo, {id,...} = editando
  const [editing, setEditing] = useState<EducationItem | Record<string, never> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<EducationItem | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`therapists/me/education/${id}`).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-therapist-profile'] });
      setConfirmDelete(null);
    },
  });

  async function openFile(id: string) {
    try {
      const { url } = await api.get(`therapists/me/education/${id}/file-url`).json<{ url: string }>();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // silencioso — botão só aparece quando há arquivo, falha é raro
    }
  }

  const sorted = [...items].sort((a, b) => {
    // FB-4 — ano crescente (cronologia de carreira)
    const ya = a.year ?? Infinity;
    const yb = b.year ?? Infinity;
    if (ya !== yb) return ya - yb;
    const pa = LEVEL_PRIORITY[a.level] ?? 99;
    const pb = LEVEL_PRIORITY[b.level] ?? 99;
    return pa - pb;
  });

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <header className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Títulos e formação</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Adicione sua graduação, especializações, cursos e demais títulos. Os clientes verão
            apenas a descrição (nível, curso, instituição e ano); os arquivos anexados ficam
            visíveis somente para você.
          </p>
        </div>
        <Button
          onClick={() => setEditing({})}
          className="bg-teal-500 hover:bg-teal-600 text-white shrink-0"
        >
          + Adicionar
        </Button>
      </header>

      {sorted.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8 border border-dashed border-slate-200 rounded-xl">
          Você ainda não cadastrou nenhum título.
        </p>
      ) : (
        <ul className="space-y-2">
          {sorted.map((e) => (
            <li key={e.id} className="rounded-xl border border-slate-200 px-4 py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                    {LEVEL_LABEL[e.level] ?? e.level}
                  </span>
                  {e.year && <span className="text-xs text-slate-400">{e.year}</span>}
                </div>
                <p className="font-medium text-slate-800 break-words">{e.course}</p>
                <p className="text-sm text-slate-500 break-words">{e.institution}</p>
                {e.fileName && (
                  <button
                    type="button"
                    onClick={() => openFile(e.id)}
                    className="text-xs text-teal-600 hover:text-teal-700 mt-1.5 inline-flex items-center gap-1"
                  >
                    📎 {e.fileName}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setEditing(e)}
                  className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(e)}
                  className="text-xs text-red-500 hover:text-red-600 px-2 py-1"
                >
                  Remover
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <EducationModal
          initial={('id' in editing ? editing : null) as EducationItem | null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['my-therapist-profile'] });
            setEditing(null);
          }}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl">
            <h3 className="font-bold text-slate-800 mb-2">Remover título?</h3>
            <p className="text-sm text-slate-500 mb-5">
              {confirmDelete.fileName
                ? 'O arquivo anexado também será apagado e não poderá ser recuperado.'
                : 'Esta ação não pode ser desfeita.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleteMut.isLoading}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md"
              >
                Cancelar
              </button>
              <button
                onClick={() => deleteMut.mutate(confirmDelete.id)}
                disabled={deleteMut.isLoading}
                className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-md disabled:opacity-60"
              >
                {deleteMut.isLoading ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function EducationModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: EducationItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!initial?.id;
  const [level, setLevel] = useState(initial?.level ?? 'GRADUACAO');
  const [course, setCourse] = useState(initial?.course ?? '');
  const [institution, setInstitution] = useState(initial?.institution ?? '');
  const [year, setYear] = useState<string>(initial?.year ? String(initial.year) : '');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isValid = course.trim().length >= 2 && institution.trim().length >= 2;

  const saveMut = useMutation({
    mutationFn: async () => {
      if (isEdit && initial) {
        // PATCH só campos texto (trocar arquivo = remover + adicionar de novo)
        return api.patch(`therapists/me/education/${initial.id}`, {
          json: {
            level: level.trim(),
            course: course.trim(),
            institution: institution.trim(),
            year: year ? Number(year) : null,
          },
        }).json<any>();
      } else {
        const fd = new FormData();
        fd.set('level', level.trim());
        fd.set('course', course.trim());
        fd.set('institution', institution.trim());
        if (year) fd.set('year', year);
        if (file) fd.set('file', file);
        return api.post('therapists/me/education', { body: fd }).json<any>();
      }
    },
    onSuccess: () => onSaved(),
    onError: async (err: any) => {
      try {
        const body = await err?.response?.json?.();
        setError(body?.message ?? 'Erro ao salvar. Tente novamente.');
      } catch {
        setError('Erro ao salvar. Tente novamente.');
      }
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > 5 * 1024 * 1024) {
      setError('Arquivo excede 5 MB');
      setFile(null);
      e.target.value = '';
      return;
    }
    setError(null);
    setFile(f);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl my-8">
        <h3 className="font-bold text-slate-800 mb-4">
          {isEdit ? 'Editar título' : 'Adicionar título'}
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nível</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="w-full border border-slate-200 rounded-md h-10 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"
            >
              {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Curso / título *</label>
            <input
              type="text"
              value={course}
              onChange={(e) => setCourse(e.target.value)}
              maxLength={200}
              placeholder="Ex: Psicologia"
              className="w-full border border-slate-200 rounded-md h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Instituição *</label>
            <input
              type="text"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              maxLength={200}
              placeholder="Ex: USP"
              className="w-full border border-slate-200 rounded-md h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Ano de conclusão</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              min={1950}
              max={new Date().getFullYear() + 1}
              placeholder="Opcional"
              className="w-full border border-slate-200 rounded-md h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
          </div>

          {!isEdit && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Certificado (opcional)
              </label>
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                onChange={handleFileChange}
                className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
              />
              <p className="text-xs text-slate-400 mt-1">PDF, PNG ou JPG (até 5 MB)</p>
            </div>
          )}

          {isEdit && initial?.fileName && (
            <p className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2">
              Arquivo atual: <strong>{initial.fileName}</strong>. Pra trocar o arquivo, remova este
              título e cadastre de novo.
            </p>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saveMut.isLoading}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => saveMut.mutate()}
            disabled={!isValid || saveMut.isLoading}
            className="px-4 py-2 text-sm bg-teal-500 hover:bg-teal-600 text-white rounded-md disabled:opacity-60"
          >
            {saveMut.isLoading ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Adicionar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AvailabilityCard({ therapistId }: { therapistId: string }) {
  void therapistId;
  const qc = useQueryClient();
  const { data: existing = [] } = useQuery({
    queryKey: ['my-availability'],
    queryFn: () => api.get('therapists/me/availability').json<any[]>(),
  });

  // Estado local: 7 dias na ordem Seg→Dom (index i=0..6 → DAY_LABELS[i]).
  // dayOfWeek do schema: 0=Domingo..6=Sábado (padrão JS Date.getDay).
  const [slots, setSlots] = useState<DaySlots[]>([]);
  // FB-1 — vigência da agenda (aplica-se a todo o conjunto). Ambos vazio = "vale sempre".
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');

  useEffect(() => {
    const init: DaySlots[] = [];
    for (let i = 0; i < 7; i++) {
      const dow = i === 6 ? 0 : i + 1;
      const windows = (existing as any[])
        .filter((e) => e.dayOfWeek === dow)
        .map((e) => ({ startTime: toHourOnly(e.startTime), endTime: toHourOnly(e.endTime) }))
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
      init.push({
        dayOfWeek: dow,
        enabled: windows.length > 0,
        windows: windows.length > 0 ? windows : [{ startTime: '09:00', endTime: '18:00' }],
      });
    }
    setSlots(init);
    // Se todas as rules têm a mesma vigência, pré-popula. Caso divergir, deixa vazio.
    const firstWithVf = (existing as any[]).find((e) => e.validFrom);
    const firstWithVu = (existing as any[]).find((e) => e.validUntil);
    setValidFrom(firstWithVf?.validFrom ? String(firstWithVf.validFrom).slice(0, 10) : '');
    setValidUntil(firstWithVu?.validUntil ? String(firstWithVu.validUntil).slice(0, 10) : '');
  }, [existing]);

  // F2 — valida start<end em cada janela e ausência de overlap entre janelas
  // do mesmo dia. computeAvailability no backend duplicaria slots se houver
  // overlap, então essa checagem é o gatekeeper antes do save.
  const invalidDays = (() => {
    const bad = new Set<number>();
    for (const s of slots) {
      if (!s.enabled || s.windows.length === 0) continue;
      for (const w of s.windows) {
        if (w.startTime >= w.endTime) { bad.add(s.dayOfWeek); break; }
      }
      if (bad.has(s.dayOfWeek)) continue;
      const sorted = [...s.windows].sort((a, b) => a.startTime.localeCompare(b.startTime));
      for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].endTime > sorted[i + 1].startTime) { bad.add(s.dayOfWeek); break; }
      }
    }
    return bad;
  })();

  const hasError = invalidDays.size > 0;

  const save = useMutation({
    mutationFn: (data: any) => api.put('therapists/me/availability', { json: data }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-availability'] }),
  });

  function toggleDay(idx: number) {
    setSlots((s) => s.map((x, i) => (i === idx ? { ...x, enabled: !x.enabled } : x)));
  }

  function updateWindow(dayIdx: number, winIdx: number, patch: Partial<Window>) {
    setSlots((s) =>
      s.map((x, i) =>
        i === dayIdx
          ? { ...x, windows: x.windows.map((w, j) => (j === winIdx ? { ...w, ...patch } : w)) }
          : x,
      ),
    );
  }

  function addWindow(dayIdx: number) {
    setSlots((s) =>
      s.map((x, i) =>
        i === dayIdx
          ? { ...x, windows: [...x.windows, { startTime: '09:00', endTime: '10:00' }] }
          : x,
      ),
    );
  }

  function removeWindow(dayIdx: number, winIdx: number) {
    setSlots((s) =>
      s.map((x, i) =>
        i === dayIdx ? { ...x, windows: x.windows.filter((_, j) => j !== winIdx) } : x,
      ),
    );
  }

  const validityInvalid = !!(validFrom && validUntil && validFrom > validUntil);

  function commit() {
    if (hasError || validityInvalid) return;
    save.mutate({
      slots: slots
        .filter((s) => s.enabled && s.windows.length > 0)
        .flatMap((s) =>
          s.windows.map((w) => ({
            dayOfWeek: s.dayOfWeek,
            startTime: w.startTime,
            endTime: w.endTime,
          })),
        ),
      validFrom: validFrom || null,
      validUntil: validUntil || null,
    });
  }

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <h2 className="text-lg font-bold text-slate-800">Disponibilidade semanal</h2>
      <p className="text-sm text-slate-500 mt-1 mb-5">
        Marque os dias e horários em que você atende. Você pode adicionar múltiplas
        janelas no mesmo dia (ex: 8h–12h e 13h–18h). As sessões são oferecidas em
        horas cheias.
      </p>

      {/* FB-1 — vigência opcional. Ambos vazios = agenda vale sempre. */}
      <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
        <p className="text-xs font-semibold text-slate-600 mb-2">
          Período de vigência <span className="text-slate-400 font-normal">(opcional)</span>
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] text-slate-500">Válido a partir de</span>
            <input
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-md h-9 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
          </label>
          <label className="block">
            <span className="text-[11px] text-slate-500">Válido até</span>
            <input
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-md h-9 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
          </label>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Se deixar em branco, esta agenda vale sempre. Preencha se quiser que ela expire
          em uma data específica (ex.: mudar de full-time para part-time em 3 meses).
        </p>
        {validityInvalid && (
          <p className="text-xs text-red-600 mt-2">"Válido a partir de" precisa ser anterior a "até".</p>
        )}
      </div>

      <div className="space-y-3">
        {slots.map((s, i) => {
          const invalid = invalidDays.has(s.dayOfWeek);
          return (
            <div
              key={s.dayOfWeek}
              className={cn(
                'rounded-xl border px-4 py-3',
                s.enabled ? 'bg-white border-slate-200' : 'bg-slate-50/60 border-slate-100',
                invalid && 'border-red-300 bg-red-50/40',
              )}
            >
              <div className="flex items-start gap-4 flex-wrap">
                <button
                  type="button"
                  onClick={() => toggleDay(i)}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition w-32 justify-center',
                    s.enabled
                      ? 'bg-teal-500 text-white border-teal-500'
                      : 'bg-white text-slate-500 border-slate-200',
                  )}
                >
                  <span className={cn('size-2 rounded-full', s.enabled ? 'bg-white' : 'bg-slate-300')} />
                  {DAY_LABELS[i]}
                </button>

                {s.enabled && (
                  <div className="flex-1 min-w-[260px] space-y-2">
                    {s.windows.map((w, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <select
                          value={w.startTime}
                          onChange={(e) => updateWindow(i, j, { startTime: e.target.value })}
                          className="border rounded-md h-9 px-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white border-slate-200"
                        >
                          {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <span className="text-sm text-slate-500">até</span>
                        <select
                          value={w.endTime}
                          onChange={(e) => updateWindow(i, j, { endTime: e.target.value })}
                          className="border rounded-md h-9 px-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white border-slate-200"
                        >
                          {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeWindow(i, j)}
                          aria-label="Remover horário"
                          className="ml-1 size-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-red-500"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addWindow(i)}
                      className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                    >
                      + Adicionar horário
                    </button>
                  </div>
                )}
              </div>
              {invalid && (
                <p className="mt-2 text-xs text-red-600">
                  Horários inválidos: cada janela precisa ter início antes do fim e não pode se sobrepor às outras.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        {save.isSuccess && !hasError && <span className="text-sm text-teal-600">Disponibilidade salva.</span>}
        <Button
          onClick={commit}
          disabled={save.isPending || hasError || validityInvalid}
          className="bg-teal-500 hover:bg-teal-600 text-white ml-auto"
        >
          {save.isPending ? 'Salvando...' : 'Salvar disponibilidade'}
        </Button>
      </div>
    </section>
  );
}

/* ─── CLIENTE ──────────────────────────────────────────────────────────────── */

function ClientProfile() {
  const { data: profile } = useQuery({
    queryKey: ['my-client-profile'],
    queryFn: () => api.get('clients/me/profile').json<any>(),
  });
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });

  const avatar = profile?.avatarUrl ?? me?.image ?? null;
  const name = profile?.user?.name ?? me?.name ?? '';
  const initial = (name ?? '?').charAt(0).toUpperCase();

  const modalityLabel = profile?.preferredModality === 'ONLINE'
    ? 'ONLINE'
    : profile?.preferredModality === 'PRESENCIAL'
      ? 'PRESENCIAL'
      : profile?.preferredModality === 'HYBRID'
        ? 'HÍBRIDO'
        : '—';

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        {avatar ? (
          <img src={avatar} alt="" className="size-16 rounded-full object-cover ring-2 ring-teal-100" />
        ) : (
          <div className="size-16 rounded-full bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white font-bold text-2xl ring-2 ring-teal-100">
            {initial}
          </div>
        )}
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">{name}</h1>
          <p className="text-slate-500 text-sm mt-0.5">Seus dados pessoais — sempre privados</p>
        </div>
      </div>

      {/* Dados privados (read-only) */}
      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <header className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <h2 className="text-lg font-bold text-slate-800">Dados privados</h2>
          <Link to="/onboarding/client">
            <Button variant="outline" className="text-sm">Editar</Button>
          </Link>
        </header>

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
          <Info label="Cidade/UF" value={[profile?.city, profile?.state].filter(Boolean).join(', ') || '—'} />
          <Info label="Modalidade preferida" value={modalityLabel} />
          <Info
            label="Data de nascimento"
            value={profile?.birthDate ? new Date(profile.birthDate).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}
          />
          <Info label="Telefone" value={profile?.phone || '—'} />
          <Info
            label="Confirmação por WhatsApp"
            value={profile?.whatsappOptIn ? 'Ativada' : 'Desativada'}
          />
        </div>
      </section>

      {/* T-02: opt-in de gravação/transcrição (cliente) */}
      <RecordingConsentCard profile={profile} />

      {/* Notificações no navegador */}
      <PushNotificationsCard />

      {/* LGPD */}
      <LgpdExportCard />
    </div>
  );
}

/**
 * T-02 — Cartão de opt-in do cliente pra gravação/transcrição de sessões.
 * A feature SÓ roda quando este consentimento + o opt-in do terapeuta
 * estão ambos ativos (T-03).
 */
function RecordingConsentCard({ profile }: { profile: any }) {
  const qc = useQueryClient();
  const consent = !!profile?.recordingConsent;
  const m = useMutation({
    mutationFn: (next: boolean) => clientsApi.setRecordingConsent(next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-client-profile'] }),
  });
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <header>
        <h2 className="text-lg font-bold text-slate-800">Gravação e transcrição de sessões</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Permite que seu profissional grave e transcreva suas sessões automaticamente. Pode desativar a qualquer momento.
        </p>
      </header>
      <div className="flex items-start gap-3">
        <input
          id="recordingConsent"
          type="checkbox"
          checked={consent}
          disabled={m.isLoading}
          onChange={(e) => m.mutate(e.target.checked)}
          className="mt-0.5 size-4"
        />
        <label htmlFor="recordingConsent" className="text-sm text-slate-700 leading-relaxed">
          <strong>Eu autorizo</strong> que minhas sessões sejam gravadas e transcritas (online via Google Meet
          ou presencial via celular do profissional). Os dados são tratados como informação de saúde sensível
          (LGPD Art. 11) — só o profissional tem acesso à transcrição. O áudio é apagado em até 7 dias após
          a transcrição.
        </label>
      </div>
      {m.isError && <p className="text-xs text-red-600">Não foi possível atualizar. Tente de novo.</p>}
    </section>
  );
}

function PushNotificationsCard() {
  // Solução leve em dev: solicita permissão do browser pra notificações.
  // Quando o módulo de Web Push tiver subscribe real, este botão vira o subscribe.
  const [perm, setPerm] = useState<NotificationPermission | null>(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : null,
  );
  const enabled = perm === 'granted';
  const enable = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    const result = await Notification.requestPermission();
    setPerm(result);
  };
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6 flex items-start justify-between gap-4 flex-wrap">
      <div className="flex-1 min-w-[260px]">
        <h2 className="text-lg font-bold text-slate-800">Notificações no navegador</h2>
        <p className="text-sm text-slate-500 mt-1">
          Receba avisos de novos agendamentos e lembretes mesmo com a aba fechada.
        </p>
      </div>
      <Button
        onClick={enable}
        disabled={enabled}
        className="bg-teal-500 hover:bg-teal-600 disabled:bg-slate-200 disabled:text-slate-500 text-white"
      >
        {enabled ? '✓ Ativado' : 'Ativar'}
      </Button>
    </section>
  );
}

function LgpdExportCard() {
  const apiBase = (import.meta as any).env?.VITE_API_URL ?? '/v1';
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <h2 className="text-lg font-bold text-slate-800">Privacidade &amp; dados (LGPD)</h2>
      <p className="text-sm text-slate-500 mt-1 mb-4">
        Você pode baixar todos os seus dados pessoais armazenados na plataforma a qualquer momento.
      </p>
      <a
        href={`${apiBase}/me/export`}
        download
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-200 hover:border-teal-400 hover:bg-teal-50 text-sm font-medium text-slate-700 transition"
      >
        📥 Baixar meus dados (JSON)
      </a>
    </section>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-sm text-slate-800 mt-1 whitespace-pre-line">{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
